import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Sparkle, PaperPlaneRight, Plus, Trash, ChatCircleDots } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTERS = [
  "Why are we not ready for Australia?",
  "What single investment raises GMARI™ the most?",
  "Which markets can we submit to this quarter?",
  "Summarize our biggest evidence risks",
  "How does our GVD alignment affect readiness?",
];

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const bold = (s) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

const Md = ({ text }) => (
  <div className="space-y-1.5">
    {text.split("\n").map((line, i) => {
      if (!line.trim()) return null;
      if (/^#{1,4}\s/.test(line))
        return <p key={i} className="font-semibold text-slate-900 pt-1" dangerouslySetInnerHTML={{ __html: bold(line.replace(/^#+\s/, "")) }} />;
      if (/^\s*[-•*]\s/.test(line))
        return (
          <div key={i} className="flex gap-2 pl-1">
            <span className="text-emerald-600 shrink-0">•</span>
            <span dangerouslySetInnerHTML={{ __html: bold(line.replace(/^\s*[-•*]\s/, "")) }} />
          </div>
        );
      if (/^\s*\d+\.\s/.test(line))
        return <p key={i} className="pl-1" dangerouslySetInnerHTML={{ __html: bold(line) }} />;
      return <p key={i} dangerouslySetInnerHTML={{ __html: bold(line) }} />;
    })}
  </div>
);

export default function CopilotPage() {
  const { productId, product } = useProduct();
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const skipNextLoad = useRef(false);

  const loadSessions = () =>
    axios.get(`${API}/copilot/${productId}/sessions`).then((r) => { setSessions(r.data); return r.data; });

  useEffect(() => {
    if (!productId) return;
    setSessionId(null);
    setMessages([]);
    loadSessions().catch(console.error);
  }, [productId]);

  useEffect(() => {
    if (!sessionId) return;
    if (skipNextLoad.current) { skipNextLoad.current = false; return; }
    axios.get(`${API}/copilot/sessions/${sessionId}/messages`).then((r) => setMessages(r.data)).catch(console.error);
  }, [sessionId]);

  useEffect(() => {
    if (messages.length && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const newSession = async () => {
    const r = await axios.post(`${API}/copilot/${productId}/sessions`);
    setSessions((s) => [r.data, ...s]);
    setSessionId(r.data.id);
    setMessages([]);
    return r.data.id;
  };

  const removeSession = async (e, id) => {
    e.stopPropagation();
    await axios.delete(`${API}/copilot/sessions/${id}`);
    setSessions((s) => s.filter((x) => x.id !== id));
    if (sessionId === id) { setSessionId(null); setMessages([]); }
  };

  const send = async (q) => {
    const question = (q || text).trim();
    if (!question || busy) return;
    setText("");
    setBusy(true);
    let sid = sessionId;
    try {
      if (!sid) { skipNextLoad.current = true; sid = await newSession(); }
      setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
      const res = await fetch(`${API}/copilot/sessions/${sid}/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: question }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const evt of events) {
          if (!evt.startsWith("data: ")) continue;
          const ev = JSON.parse(evt.slice(6));
          if (ev.error) throw new Error(ev.error);
          if (ev.delta)
            setMessages((m) => {
              const last = m[m.length - 1];
              if (!last || last.role !== "assistant") return [...m, { role: "assistant", content: ev.delta }];
              return [...m.slice(0, -1), { ...last, content: last.content + ev.delta }];
            });
        }
      }
      loadSessions().catch(() => {});
    } catch (err) {
      toast.error(err.message || "Copilot request failed");
      setMessages((m) => (m.length && m[m.length - 1].role === "assistant" && !m[m.length - 1].content ? m.slice(0, -1) : m));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell overline="Decision Layer · Conversational Strategy" title="AI Strategy Copilot" pageId="copilot">
      <div data-testid="copilot-page" className="flex gap-6 items-stretch" style={{ height: "calc(100vh - 250px)", minHeight: 480 }}>
        <aside className="w-64 shrink-0 border border-slate-200 bg-white flex flex-col">
          <div className="p-3 border-b border-slate-200">
            <button data-testid="copilot-new-session-btn" onClick={newSession}
              className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-700 transition-colors">
              <Plus size={14} weight="bold" /> New conversation
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessions.length === 0 && <p className="text-xs text-slate-400 p-4">No conversations yet for {product?.name || "this product"}.</p>}
            {sessions.map((s) => (
              <div key={s.id} data-testid="copilot-session-item" onClick={() => setSessionId(s.id)}
                className={`group flex items-start gap-2 px-3 py-2.5 cursor-pointer border-b border-slate-100 ${sessionId === s.id ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                <ChatCircleDots size={14} className="text-slate-400 mt-0.5 shrink-0" />
                <p className="flex-1 text-xs text-slate-700 leading-snug line-clamp-2">{s.title}</p>
                <button data-testid="copilot-delete-session-btn" onClick={(e) => removeSession(e, s.id)} title="Delete"
                  className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 shrink-0">
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        </aside>

        <div className="flex-1 min-w-0 border border-slate-200 bg-white flex flex-col">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <Sparkle size={28} weight="fill" className="text-emerald-500 mb-3" />
                <p className="font-heading font-bold text-lg">Ask anything about your global strategy</p>
                <p className="text-sm text-slate-500 mt-1 max-w-md">Grounded in {product?.name || "your product"}'s live readiness, gaps, evidence inventory and intelligence events.</p>
                <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-xl">
                  {STARTERS.map((s, i) => (
                    <button key={s} data-testid={`copilot-starter-${i}`} onClick={() => send(s)}
                      className="text-xs text-slate-600 border border-slate-200 px-3 py-2 hover:border-emerald-500 hover:text-slate-900 transition-colors">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`copilot-message-${m.role}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] text-sm leading-relaxed px-4 py-3 ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-50 border border-slate-200 text-slate-800"}`}>
                  {m.role === "assistant"
                    ? (m.content ? <Md text={m.content} /> : <span className="inline-flex items-center gap-2 text-slate-400 text-xs"><Sparkle size={13} className="animate-pulse text-emerald-500" /> MAI Copilot is reasoning over your knowledge graph…</span>)
                    : m.content}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-slate-200 p-3 flex gap-2">
            <input data-testid="copilot-input" value={text} onChange={(e) => setText(e.target.value)} disabled={busy}
              placeholder={busy ? "MAI Copilot is answering…" : 'e.g. "Why are we not ready for Australia?"'}
              className="flex-1 text-sm px-4 py-3 bg-slate-50 border border-slate-200 focus:outline-none focus:border-emerald-500 disabled:opacity-60" />
            <button data-testid="copilot-send-btn" disabled={busy || !text.trim()}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white px-5 flex items-center transition-colors">
              <PaperPlaneRight size={16} weight="bold" />
            </button>
          </form>
        </div>
      </div>
    </PageShell>
  );
}
